import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useMagnetic } from "@/components/effects";

export default function Problem() {
  const magRef = useMagnetic(0.28);
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-24 md:py-32 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-12">
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
            §02 · The Problem
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
          <h2
            data-testid="problem-headline"
            className="lg:col-span-8 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-snug"
          >
            The industry gave you two bad choices. <span className="text-zinc-500">We're building the third.</span>
          </h2>
          <p className="lg:col-span-4 text-base leading-relaxed text-zinc-400 font-light">
            On one extreme, &quot;vibe coding&quot; ships fragile prototypes that
            break under real users. On the other, traditional development buries
            you in quotes, gantt charts, and a six-month timeline. Neither
            respects your time, your stress, or your business.
          </p>
        </div>

        {/* The Third Way — concept image */}
        <figure
          data-testid="third-way-image"
          className="mb-16 fade-up"
        >
          <img
            src="/logos/two-bad-choices.jpg"
            alt="Two bad choices. Both will cost you. — Vibe Coding (fragile, untestable, will break) vs. Traditional Dev ($10,000/month, part-time, six-month timeline)."
            className="w-full h-auto block"
            loading="lazy"
          />
        </figure>

        {/* Spectrum */}
        <div
          data-testid="spectrum-graphic"
          className="relative border border-white/10 bg-white/[0.02] p-8 md:p-12"
        >
          <div className="grid grid-cols-12 items-center gap-6 relative">
            {/* Left label */}
            <div className="col-span-12 md:col-span-3">
              <div className="text-xs uppercase tracking-[0.22em] text-red-400/80 font-mono mb-2">
                Too Fast
              </div>
              <div className="font-heading text-xl font-light text-white">Vibe Coding</div>
              <div className="text-sm text-zinc-500 mt-1 font-light">Fragile. Untestable. Will break.</div>
            </div>

            {/* Spectrum bar */}
            <div className="col-span-12 md:col-span-6">
              <div className="relative h-2 w-full bg-gradient-to-r from-red-500/60 via-yellow-400 to-blue-500/60">
                {/* Marker */}
                <div className="absolute top-1/2 -translate-y-1/2" style={{ left: "32%" }}>
                  <div className="w-px h-12 bg-yellow-400 -translate-y-5" />
                  <div className="absolute -translate-x-1/2 -translate-y-12 whitespace-nowrap">
                    <div className="border border-yellow-400/60 bg-black px-3 py-1.5">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-yellow-400 font-mono">
                        You are here
                      </div>
                      <div className="font-heading text-sm text-white">Invisible System Design</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-600 mt-3">
                <span>day 0</span>
                <span>week 1</span>
                <span>month 3</span>
                <span>quarter 2</span>
              </div>
            </div>

            {/* Right label */}
            <div className="col-span-12 md:col-span-3 md:text-right">
              <div className="text-xs uppercase tracking-[0.22em] text-blue-400/80 font-mono mb-2">
                Too Slow
              </div>
              <div className="font-heading text-xl font-light text-white">Traditional Dev</div>
              <div className="text-sm text-zinc-500 mt-1 font-light">Expensive. Bureaucratic. Late.</div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 grid md:grid-cols-3 gap-8">
            {[
              { k: "Vibe Coding", v: "Speed without rigor. Hallucinated APIs. No tests. Production catches fire." },
              { k: "Invisible System Design", v: "Engineered velocity. Human oversight. Real systems thinking, faster than you expected.", accent: true },
              { k: "Traditional Dev", v: "Six-figure SOWs, twelve-week discovery, and an app nobody opens." },
            ].map((c) => (
              <div
                key={c.k}
                className={`p-6 border ${c.accent ? "border-yellow-400/40 bg-yellow-400/[0.04]" : "border-white/10"}`}
              >
                <div className={`text-xs uppercase tracking-[0.22em] font-mono mb-3 ${c.accent ? "text-yellow-400" : "text-zinc-500"}`}>
                  {c.k}
                </div>
                <div className="text-sm leading-relaxed text-zinc-400 font-light">{c.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA to Solution page */}
        <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-10">
          <div className="flex-1">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
              The third way →
            </div>
            <h3 className="font-heading text-2xl sm:text-3xl font-light tracking-tight text-white">
              See how we solve it.
            </h3>
          </div>
          <Link
            ref={magRef}
            to="/approach"
            data-testid="problem-to-solution-cta"
            className="group inline-flex items-center gap-3 bg-yellow-400 text-black px-7 py-4 text-xs uppercase tracking-[0.22em] font-medium hover:bg-yellow-500 shrink-0"
          >
            See the Solution
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
          </Link>
        </div>
      </div>
    </section>
  );
}
